/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ArrowUpIcon,
  LightBulbIcon,
  MicrophoneIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import React, { type Dispatch, type SetStateAction } from 'react';

import { cn } from '@/lib/utils';
import ModeSelector from '@/sections/insights/aiAssistant/ModeSelector';
import type { AIAssistantMode } from '@/sections/insights/aiAssistant/types';

import TipsMenu from './TipsMenu';

interface InputFormProps {
  newMessage: string;
  setNewMessage: (msg: string) => void;
  mode: AIAssistantMode;
  onModeChange: (mode: AIAssistantMode) => void;
  isInputDisabled: boolean;
  isWaitingForResponse: boolean;
  // Planner doing work → Send hidden, Stop shown (existing behavior).
  isPlannerRunning: boolean;
  // Planner idle but background jobs running → Stop shown ALONGSIDE Send.
  showBackgroundStop: boolean;
  isStopping: boolean;
  onStop: () => void;
  isRecording: boolean;
  isAIDisabledButBypassed: boolean;
  canUseAgentMode: boolean;
  isPraetorianUser: boolean;
  isSubagent: boolean;
  isSubagentFinished: boolean;
  isSubagentQueued: boolean;
  activeAgent: string | null;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  commandMenuRef: React.RefObject<HTMLDivElement | null>;
  // Command menu
  showCommandMenu: boolean;
  filteredCommands: any[];
  menuType: string;
  // Popovers
  showTipsMenu: boolean;
  setShowTipsMenu: Dispatch<SetStateAction<boolean>>;
  // Handlers
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onToggleRecording: () => void;
  onSelectCommand: (cmd: string) => void;
  onReplaceCurrentWord: (word: string, data?: any) => string;
  onHistoryNavigation: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  maxTextareaHeight: number;
}

export function InputForm({
  newMessage,
  setNewMessage,
  mode,
  onModeChange,
  isInputDisabled,
  isWaitingForResponse,
  isPlannerRunning,
  showBackgroundStop,
  isStopping,
  onStop,
  isRecording,
  isAIDisabledButBypassed,
  canUseAgentMode,
  isPraetorianUser,
  isSubagent,
  isSubagentFinished,
  isSubagentQueued,
  textareaRef,
  commandMenuRef,
  showCommandMenu,
  filteredCommands,
  menuType,
  showTipsMenu,
  setShowTipsMenu,
  onSubmit,
  onToggleRecording,
  onSelectCommand,
  onReplaceCurrentWord,
  onHistoryNavigation,
  maxTextareaHeight,
}: InputFormProps) {
  return (
    <div className="px-6 pb-6">
      <form onSubmit={onSubmit} className="relative">
        <div
          onClick={() => textareaRef.current?.focus()}
          className={`bg-chariot-background-primary flex cursor-text items-center gap-3 rounded-full border px-4 py-3 transition-colors ${
            mode === 'agent'
              ? 'border-chariot-brand-primary hover:border-chariot-brand-primary focus-within:border-chariot-brand-primary'
              : 'border-chariot-stroke hover:border-chariot-brand-primary focus-within:border-chariot-brand-primary'
          }`}
        >
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setShowTipsMenu(prev => !prev);
            }}
            className="tips-menu-button text-chariot-text-secondary hover:text-chariot-text-primary flex-shrink-0 transition-colors"
            title="Show search shortcuts"
          >
            <LightBulbIcon className="size-5" />
          </button>
          {canUseAgentMode && !isAIDisabledButBypassed && (
            <ModeSelector
              mode={mode}
              onModeChange={onModeChange}
              disabled={isWaitingForResponse}
              showAuto={isPraetorianUser}
            />
          )}
          <textarea
            ref={textareaRef}
            value={newMessage}
            disabled={isInputDisabled}
            onChange={e => setNewMessage(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                // Always prevent the newline. Only submit when the planner is
                // idle — this agrees with Send being hidden during a planner
                // run, while still allowing Enter during idle+background-jobs
                // (where Send is shown).
                e.preventDefault();
                if (!isPlannerRunning) {
                  onSubmit(e as unknown as React.FormEvent);
                }
              } else {
                onHistoryNavigation(e);
              }
            }}
            placeholder={
              isSubagentFinished
                ? 'This subagent conversation has completed'
                : isSubagentQueued
                  ? 'Subagent is starting up'
                  : isSubagent
                    ? 'Provide subagent guidance'
                    : mode === 'auto'
                      ? 'Bar your windows. Lock your doors. Hannibal ad portas...'
                      : mode === 'agent'
                        ? 'Run a task against your attack surface'
                        : 'Ask anything about your attack surface'
            }
            className="text-chariot-text-primary placeholder-chariot-text-secondary flex-1 resize-none bg-transparent text-sm focus:outline-hidden"
            rows={1}
            style={{
              minHeight: '24px',
              maxHeight: `${maxTextareaHeight}px`,
            }}
          />
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onToggleRecording();
            }}
            className={`flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
              isRecording
                ? 'bg-error hover:bg-error/90 animate-pulse text-white'
                : 'text-chariot-text-secondary hover:text-chariot-text-primary'
            }`}
            title={isRecording ? 'Stop recording' : 'Voice input'}
          >
            <MicrophoneIcon className="size-4" />
          </button>
          {/* Stop: shown while the planner runs OR background jobs run. It is a
              sibling of Send, never a replacement — so in the idle+jobs state
              both render (order [Stop][Send]). One Stop → POST /planner/stop,
              the global cancel of all non-terminal jobs for the conversation. */}
          {(isPlannerRunning || showBackgroundStop) && (
            <button
              type="button"
              disabled={isStopping}
              onClick={e => {
                e.stopPropagation();
                onStop();
              }}
              aria-label={
                showBackgroundStop && !isPlannerRunning
                  ? 'Stop all running scans'
                  : 'Stop'
              }
              title={isStopping ? 'Stopping…' : 'Stop'}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90 flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed',
                isStopping && 'animate-pulse opacity-70'
              )}
            >
              <StopIcon className="size-4" />
            </button>
          )}
          {/* Send: hidden ONLY while the planner is actively running. When only
              background jobs run, Send stays available so the user can send
              follow-ups. */}
          {!isPlannerRunning && (
            <button
              type="submit"
              disabled={!newMessage.trim() || isInputDisabled}
              onClick={e => e.stopPropagation()}
              aria-label="Send message"
              className={cn(
                'bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 flex size-8 flex-shrink-0 items-center justify-center rounded-full text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40'
              )}
            >
              <ArrowUpIcon className="size-4" />
            </button>
          )}
        </div>

        {/* Command/Search menu - show above input (since input is at bottom of screen) */}
        {showCommandMenu &&
          filteredCommands.length > 0 &&
          (menuType === 'search' || canUseAgentMode) && (
            <div
              ref={commandMenuRef}
              className="command-menu bg-chariot-background-primary border-chariot-stroke absolute right-0 bottom-full left-0 z-50 mb-2 max-h-80 overflow-hidden rounded-lg border shadow-lg"
            >
              <div className="max-h-80 overflow-y-auto p-2">
                {filteredCommands.map((cmd, index) => (
                  <button
                    key={`${cmd.command}-${index}`}
                    type="button"
                    onClick={() => {
                      // For entity search (@ or #), replace with JSON data if available
                      if (menuType === 'search' && cmd.data) {
                        setNewMessage(
                          onReplaceCurrentWord(cmd.command, cmd.data)
                        );
                        textareaRef.current?.focus();
                      }
                      // For search without data, just replace text
                      else if (menuType === 'search') {
                        setNewMessage(onReplaceCurrentWord(cmd.command));
                        textareaRef.current?.focus();
                      }
                      // For commands, execute action
                      else {
                        onSelectCommand(cmd.command);
                      }
                    }}
                    className="hover:bg-chariot-background-secondary w-full rounded px-3 py-2 text-left transition-colors"
                  >
                    <div className="text-chariot-text-primary text-sm font-medium">
                      {cmd.command}
                    </div>
                    <div className="text-chariot-text-secondary text-xs">
                      {cmd.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

        {/* Tips menu popover */}
        <TipsMenu
          show={showTipsMenu}
          position="above"
          onTipClick={icon => {
            setNewMessage(
              newMessage +
                (newMessage && !newMessage.endsWith(' ') ? ' ' : '') +
                icon
            );
            textareaRef.current?.focus();
            setShowTipsMenu(false);
          }}
        />
      </form>
    </div>
  );
}
